import React, { useEffect } from 'react';
import { Modal, Form, Input, Switch, Space, Typography } from 'antd';
import { UserOutlined, MailOutlined, LockOutlined, SafetyCertificateOutlined, SecurityScanOutlined, InfoCircleOutlined } from '@ant-design/icons';

const UserForm = ({ open, onCancel, onSubmit, initialValues, title, submitting }) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (open) {
      form.resetFields();
      if (initialValues) {
        form.setFieldsValue(initialValues);
      }
    }
  }, [open, initialValues, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      onSubmit(values);
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  return (
    <Modal
      open={open}
      title={title}
      okText={submitting ? "Saving..." : "Save"}
      cancelText="Cancel"
      onCancel={onCancel}
      onOk={handleSubmit}
      width={700}
      confirmLoading={submitting}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={initialValues}
        requiredMark="optional"
      >
        <Typography.Title level={5} style={{ marginBottom: 24 }}>
          User Information
        </Typography.Title>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <Form.Item
            name="email"
            label={
              <Space>
                <MailOutlined />
                <span>Email</span>
              </Space>
            }
            rules={[
              { required: true, message: 'Please input email!' },
              { type: 'email', message: 'Please input valid email!' }
            ]}
            tooltip={{
              title: "User's primary email address for login and communications",
              icon: <InfoCircleOutlined />
            }}
          >
            <Input prefix={<MailOutlined />} placeholder="Enter email address" />
          </Form.Item>

          {!initialValues && (
            <Form.Item
              name="password"
              label={
                <Space>
                  <LockOutlined />
                  <span>Password</span>
                </Space>
              }
              rules={[{ required: !initialValues, message: 'Please input password!' }]}
              tooltip={{
                title: "Set a secure password for the user account",
                icon: <InfoCircleOutlined />
              }}
            >
              <Input.Password prefix={<LockOutlined />} placeholder="Enter password" />
            </Form.Item>
          )}

          <Form.Item
            name="first_name"
            label={
              <Space>
                <UserOutlined />
                <span>First Name</span>
              </Space>
            }
            rules={[{ required: true, message: 'Please input first name!' }]}
          >
            <Input prefix={<UserOutlined />} placeholder="Enter first name" />
          </Form.Item>

          <Form.Item
            name="last_name"
            label={
              <Space>
                <UserOutlined />
                <span>Last Name</span>
              </Space>
            }
            rules={[{ required: true, message: 'Please input last name!' }]}
          >
            <Input prefix={<UserOutlined />} placeholder="Enter last name" />
          </Form.Item>
        </div>

        <Typography.Title level={5} style={{ margin: '24px 0' }}>
          Permissions and Status
        </Typography.Title>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <Form.Item
            name="is_staff"
            label={
              <Space>
                <SecurityScanOutlined />
                <span>Staff Status</span>
              </Space>
            }
            valuePropName="checked"
            tooltip={{
              title: "Staff members can access the admin panel and manage system content",
              icon: <InfoCircleOutlined />
            }}
          >
            <Switch 
              checkedChildren="Staff" 
              unCheckedChildren="Regular"
              style={{ width: 100 }}
            />
          </Form.Item>

          <Form.Item
            name="is_superuser"
            label={
              <Space>
                <SafetyCertificateOutlined />
                <span>Superuser Status</span>
              </Space>
            }
            valuePropName="checked"
            tooltip={{
              title: "Superusers have full system access and all permissions",
              icon: <InfoCircleOutlined />
            }}
          >
            <Switch 
              checkedChildren="Super" 
              unCheckedChildren="Regular"
              style={{ width: 100 }}
            />
          </Form.Item>

          <Form.Item
            name="is_active"
            label={
              <Space>
                <SecurityScanOutlined />
                <span>Account Status</span>
              </Space>
            }
            valuePropName="checked"
            tooltip={{
              title: "Active accounts can log in and use the system",
              icon: <InfoCircleOutlined />
            }}
          >
            <Switch 
              checkedChildren="Active" 
              unCheckedChildren="Inactive"
              style={{ width: 100 }}
            />
          </Form.Item>

          <Form.Item
            name="is_verified"
            label={
              <Space>
                <SafetyCertificateOutlined />
                <span>Email Status</span>
              </Space>
            }
            valuePropName="checked"
            tooltip={{
              title: "Indicates if the user's email has been verified",
              icon: <InfoCircleOutlined />
            }}
          >
            <Switch 
              checkedChildren="Verified" 
              unCheckedChildren="Unverified"
              style={{ width: 100 }}
            />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
};

export default UserForm;
